import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Menu, Phone, Users, Clock, PoundSterling, Mail, Github, Linkedin, Twitter } from 'lucide-react';
import background from '../../assets/images/background.jpg';
import about_image from '../../assets/images/about.jpg';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-10');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      title: "Industry-Leading Performance",
      icon: <Users className="h-6 w-6 text-emerald-500" />,
      content: "Our memory chips deliver unprecedented speeds and efficiency, significantly enhancing GPU performance for AI applications. With up to 10x performance improvements, your AI systems can handle more complex workloads faster than ever."
    },
    {
      title: "Comprehensive Support",
      icon: <Phone className="h-6 w-6 text-emerald-500" />,
      content: "Expert technical support and implementation guidance from our team of AI specialists. We work closely with you to ensure optimal integration and performance of our memory solutions in your specific use case."
    },
    {
      title: "Energy Efficient Design",
      icon: <Clock className="h-6 w-6 text-emerald-500" />,
      content: "Our advanced chip architecture reduces power consumption while maintaining peak performance, resulting in up to 40% energy savings compared to traditional solutions."
    },
    {
      title: "Scalable Solutions",
      icon: <PoundSterling className="h-6 w-6 text-emerald-500" />,
      content: "Flexible configurations suitable for businesses of all sizes, from startups to enterprises. Our memory solutions grow with your needs, ensuring long-term value and performance."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-black to-emerald-900">
      {/* Hero Section */}
      <section 
        className="min-h-screen xl:min-h-[60vh] relative flex items-center justify-center bg-fixed bg-cover bg-center"
        style={{ backgroundImage: `url(${background})` }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="container mx-auto px-4 relative z-10 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
          <h1 className="text-4xl md:text-6xl font-bold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-600">
            Revolutionizing AI Through Advanced Memory Solutions
          </h1>
          <p className="text-xl md:text-2xl text-center text-gray-300 max-w-3xl mx-auto">
            Empowering industries with next-generation AI memory chips for unprecedented performance and efficiency
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-black/90 backdrop-blur-md">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-white">About HYPER COGNITIVE</h2>
              <p className="text-gray-300 mb-4">
                At HYPER COGNITIVE, we're pioneering the future of AI technology through innovative memory chip solutions that transform GPU performance and capabilities.
              </p>
              <p className="text-gray-300 mb-4">
                Our breakthrough AI memory chips significantly enhance data processing speeds, enabling businesses across industries to leverage AI's full potential with reduced latency and improved efficiency.
              </p>
              <p className="text-gray-300">
                From healthcare and finance to manufacturing and entertainment, our solutions are driving innovation and economic growth by making advanced AI technology accessible to organizations of all sizes.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/5 backdrop-blur-md p-6 rounded-lg border border-white/10 hover:border-emerald-500/50 transition-all duration-300">
                <h3 className="text-4xl font-bold mb-2 text-white">10X</h3>
                <p className="text-gray-400">Performance Boost</p>
              </div>
              <div className="bg-white/5 backdrop-blur-md p-6 rounded-lg border border-white/10 hover:border-emerald-500/50 transition-all duration-300">
                <h3 className="text-4xl font-bold mb-2 text-white">50+</h3>
                <p className="text-gray-400">Industry Partners</p>
              </div>
              <div className="bg-white/5 backdrop-blur-md p-6 rounded-lg border border-white/10 hover:border-emerald-500/50 transition-all duration-300">
                <h3 className="text-4xl font-bold mb-2 text-white">40%</h3>
                <p className="text-gray-400">Energy Efficiency Gain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Impact Section */}
      <section className="py-20 bg-black/95 backdrop-blur-md">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
            <div className="bg-white/5 backdrop-blur-md p-8 rounded-lg border border-white/10">
              <img
                src={about_image}
                alt="AI Technology"
                className="w-full h-auto rounded-lg mb-6"
              />
              <blockquote className="text-gray-300">
                "HYPER COGNITIVE's memory chips have transformed our AI capabilities. The performance improvements we've seen are remarkable, enabling us to process complex AI models with unprecedented speed and efficiency. This technology has given us a significant competitive advantage in delivering cutting-edge healthcare solutions."
              </blockquote>
             
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6 text-white">Our Impact</h2>
              <div className="space-y-6 text-gray-300">
                <p>
                  Our AI memory solutions are revolutionizing multiple industries, from enabling real-time medical imaging and drug discovery in healthcare to powering high-frequency trading and fraud detection in finance.
                </p>
                <p>
                  In manufacturing, our technology enhances robotics and automation systems, while in entertainment, it enables the creation of hyper-realistic graphics and seamless virtual experiences.
                </p>
                <p>
                  By making advanced AI memory solutions accessible to businesses of all sizes, we're helping create a more innovative and competitive global economy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
          <h2 className="text-3xl font-bold text-center mb-12 text-white">Why Choose HYPER COGNITIVE?</h2>
          <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto">
            Our cutting-edge AI memory solutions are designed to transform your business operations and unlock new possibilities in AI implementation.
          </p>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {features.map((item, index) => (
              <div 
                key={index}
                className="border border-gray-800 rounded-lg overflow-hidden bg-white/5 backdrop-blur-md hover:border-emerald-500/50 transition-all duration-300"
              >
                <button
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 text-white"
                  onClick={() => setActiveSection(activeSection === item.title ? null : item.title)}
                >
                  <div className="flex items-center gap-4">
                    {item.icon}
                    <span className="font-semibold">{item.title}</span>
                  </div>
                  {activeSection === item.title ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </button>
                {activeSection === item.title && (
                  <div className="px-6 py-4 bg-white/5">
                    <p className="text-gray-300">{item.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-black/90 backdrop-blur-md">
        <div className="container mx-auto px-4 text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
          <h2 className="text-3xl font-bold mb-6 text-white">Transform Your AI Capabilities</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Ready to revolutionize your AI infrastructure? Contact our team to discuss how our memory solutions can enhance your operations.
          </p>
          <p className="text-gray-300 mb-8">
            Let's work together to unlock the full potential of your AI systems!
          </p>
          <a 
            href="#"
            className="inline-block px-8 py-3 bg-emerald-600/80 backdrop-blur-sm rounded-full hover:bg-emerald-700 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/20 text-white"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;